import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Empty } from 'antd';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import InnerCard from '../../../../../components/InnerCard/InnerCard';
import LightboxGroup from '../../../../../../../components/LightboxGroup/LightboxGroup';
import { getBlobUrl } from '../../../../../../../helpers/getBlobUrl';
import UploadButton from '../../../../../../../components/UploadButton/UploadButton';
import { CardChangeButton } from '../../../../../../../components/Card';
import Photo from './Photo';
import validateFile from '../../../../../../../helpers/validateFile';
import useAddRoomImagesMutation from '../../../../../mutations/useAddRoomImagesMutation';
import { roomQueryKeys } from '../../../../../queries/queryKeys';
import commonStyles from '../../../AboutRoom.module.scss';
import styles from '../CommonInfoBlock.module.scss';

const PhotosCard = ({ room }) => {
  const attachments = useMemo(
    () => room?.description?.attachments ?? [],
    [room]
  );

  const queryClient = useQueryClient();

  const userCanEdit = useSelector(
    (store) => store.authentication.userInfo.CanCreateOffice
  );

  const [photos, setPhotos] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const { mutate: addImageMutate, isLoading: isAddImageLoading } =
    useAddRoomImagesMutation({
      onSuccess: () => queryClient.invalidateQueries(roomQueryKeys(room?.id)),
    });

  const handleBeforeUpload = (file) => {
    const validated = validateFile(file, { onlyPicture: true });

    if (!validated) {
      return false;
    }

    return true;
  };

  const handlePhotoClick = (index) => {
    setShowPreview(true);
    setCurrentPhotoIndex(index);
  };

  const handleClosePreview = useCallback(() => {
    setShowPreview(false);
    setCurrentPhotoIndex(0);
  }, []);

  const handleAddImage = useCallback(
    ({ file }) => {
      addImageMutate({ descriptionId: room?.description?.id, files: [file] });
    },
    [room, addImageMutate]
  );

  useEffect(() => {
    let images = [];

    if (Boolean(attachments.length)) {
      images = attachments.map((attachment) => ({
        id: attachment.id,
        src: getBlobUrl(attachment.file),
      }));
    }

    setPhotos(images);
  }, [attachments]);

  const action = userCanEdit ? (
    <UploadButton
      multiple
      showUploadList={false}
      buttonComponent={
        <CardChangeButton
          text="Добавить"
          className={commonStyles.innerCardAction}
        />
      }
      disabled={isAddImageLoading}
      customRequest={handleAddImage}
      beforeUpload={handleBeforeUpload}
    />
  ) : undefined;

  return (
    <InnerCard title="Фото" action={action}>
      {Boolean(photos.length) ? (
        <ul className={styles.gallery}>
          {photos.map((photo, index) => (
            <Photo
              key={photo.id}
              photo={photo}
              style={{ height: photos.length > 1 ? '104px' : '208px' }}
              room={room}
              onPhotoClick={() => handlePhotoClick(index)}
            />
          ))}
        </ul>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет фото" />
      )}
      {showPreview && (
        <LightboxGroup
          visible={showPreview}
          onClose={handleClosePreview}
          attachments={attachments}
          attachment={attachments[currentPhotoIndex]}
          url={`/RentPlace/description/${room?.description?.id}/attachment`}
          params={{ fileName: '' }}
          imageParamField="fileName"
        />
      )}
    </InnerCard>
  );
};

export default PhotosCard;
